import {
  Consult,
  ConsultReasonForVisitEnum,
  ConsultVisitStatusEnum,
  Episode,
} from "@futurhealth/steadymd-api-client";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  User,
} from "firebase/auth";
import FirebaseFirestore, {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../conf/firebaseConfig";
import { claimSurveySession } from "./surveySessionClaimService";
import {
  FirestoreNotification,
  FirestoreSMDEpisode,
  FirestoreSMDLabOrder,
  Medication,
  Prescription,
  SMDConsult,
  SMDEpisode,
  StripePayment,
  StripeSubscription,
  SurveyQuestion,
  SurveyQuestionCollection,
  SurveySession,
  UserProfile,
} from "../typings";
import { isPastDate } from "../utils/helpers";
import {
  AsyncSessionData,
  IntakeAnswers,
  IntakeQuestion,
  UpdateAsyncSessionPayload,
} from "../typings/steadymdTypes";
import { ConsultReason, ConsultType } from "../typings/enums";
import { RefillSession } from "../typings/RefillIntake";

dayjs.extend(utc);

// Enhanced async session data interface
export interface AsyncSessionStatus {
  sessionId: string | null;
  status: "not_started" | "started" | "completed";
  canProceed: boolean;
  message?: string;
}
/**
 * Creates or updates a device token in Firestore.
 * @param userId - The user's unique identifier.
 * @param deviceData - Object containing the device's token, type, and user ID.
 */
export const createOrUpdateDeviceToken = async (
  userId: string,
  deviceData: { token: string; deviceType: string }
) => {
  const deviceRef = doc(db, "devices", deviceData.token);
  try {
    const docSnap = await getDoc(deviceRef);
    if (!docSnap.exists()) {
      await setDoc(deviceRef, {
        token: deviceData.token,
        deviceType: deviceData.deviceType,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      console.log(`Device ${deviceData.token} already exists`);
    }
  } catch (error) {
    console.error("Error managing device token:", error);
    throw error;
  }
};

export const genericConverter = <T>() => ({
  toFirestore: (data: T) => data,
  fromFirestore: (snap: FirebaseFirestore.QueryDocumentSnapshot) =>
    snap.data() as T,
});

// Function to create or update Firestore user profile
export const createOrUpdateUserProfile = async (
  user: User | null,
  userProfile: Partial<UserProfile>
): Promise<void> => {
  if (!user) throw new Error("User not authenticated");
  const userRef = doc(db, "users", user.uid).withConverter(
    genericConverter<Partial<UserProfile>>()
  );
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) {
    await setDoc(userRef, userProfile);
  } else {
    await setDoc(
      userRef,
      { ...userProfile, updatedAt: serverTimestamp() },
      { merge: true }
    );
  }
};

export const getUserProfile = async (user: User) => {
  try {
    if (!user || !user.uid) throw new Error("User not authenticated");
    const userRef = doc(db, "users", user.uid).withConverter(
      genericConverter<Partial<UserProfile>>()
    );
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      // return user data
      return userDoc.data();
    } else {
      throw new Error("no such document");
    }
  } catch (err) {
    throw new Error(`Error getting user profile: ${err}`);
  }
};

// Function to update user profile
export const updateUserProfile = async (
  userId: string,
  data: Partial<UserProfile>
): Promise<void> => {
  const userRef = doc(db, "users", userId);
  await setDoc(
    userRef,
    {
      ...data,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

// Survey Service Functions
export const updateSurveySession = async (
  sessionId: string,
  surveyData: Partial<SurveySession>
) => {
  try {
    const sessionRef = doc(db, "surveySessions", sessionId);
    await updateDoc(sessionRef, {
      ...surveyData,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error updating survey session: ", e);
    throw e;
  }
};

// TODO: this function is not used anywhere in the codebase, can it be removed?
export const updateDocument = async (
  path: string,
  pathSegments: string,
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
) => {
  try {
    const sessionRef = doc(db, path, pathSegments);
    await updateDoc(sessionRef, {
      [key]: value,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error(`Error updating ${path} ${pathSegments}`, e);
    throw e;
  }
};

export const saveSurveyResponse = async (
  sessionId: string,
  questionSlug: string,
  answer: string
) => {
  try {
    const sessionRef = doc(db, "surveySessions", sessionId);
    await updateDoc(sessionRef, {
      [`answers.${questionSlug}`]: answer,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error updating document: ", e);
  }
};

export const createOrUpdateSurveySession = async (
  sessionId: string,
  sessionData: Partial<SurveySession>
): Promise<void> => {
  const sessionRef = doc(db, "surveySessions", sessionId).withConverter(
    genericConverter<Partial<SurveySession>>()
  );
  // Intake sessions can exist only in the API until the first form submission.
  // Establish ownership before reading or writing any of their other fields.
  if (sessionData.userId) {
    await claimSurveySession(sessionId, sessionData.userId);
  }
  const sessionDoc = await getDoc(sessionRef);

  // Get current data to merge with new data for goal-months calculation
  const currentData = sessionDoc.exists() ? sessionDoc.data() : {};
  const mergedAnswers = { ...currentData?.answers, ...sessionData?.answers };

  // Calculate goal-months if we have both weight data (following MEGAFUNNEL pattern)
  if (mergedAnswers?.height_weight?.weight && mergedAnswers?.goal_weight) {
    const currentWeight = parseInt(mergedAnswers.height_weight.weight);
    const goalWeight = parseInt(mergedAnswers.goal_weight);

    if (
      !isNaN(currentWeight) &&
      !isNaN(goalWeight) &&
      currentWeight > goalWeight
    ) {
      // Calculate weeks to reach goal weight (2 lbs per week)
      const weightLoss = currentWeight - goalWeight;
      const weeks = Math.ceil(weightLoss / 2);

      // Convert weeks to months (following MEGAFUNNEL SetGoalMonths.tsx pattern)
      const calculatedMonths = Math.floor(weeks / 4);

      // Ensure we have at least 1 month
      const goalMonths = Math.max(1, calculatedMonths);

      // Add goal-months to the answers
      if (!sessionData.answers) {
        sessionData.answers = {};
      }
      sessionData.answers["goal-months"] = goalMonths.toString();
    }
  }

  if (!sessionDoc.exists()) {
    // Optional fields may contain deleteField() even on a first form save.
    await setDoc(sessionRef, sessionData, { merge: true });
  } else {
    await setDoc(
      sessionRef,
      {
        ...sessionData,
        ...(sessionData?.createdAt ? {} : { createdAt: serverTimestamp() }),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }
};

export const updateRefillSession = async (
  sessionData: Partial<SurveySession> | Partial<RefillSession>
) => {
  try {
    if (!sessionData.id) throw new Error("No session ID provided");
    const sessionRef = doc(db, "refillSessions", sessionData.id);

    await updateDoc(sessionRef, {
      ...sessionData,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error updating refill session: ", e);
    throw e;
  }
};

export const createRefillSession = async (
  sessionData: Partial<RefillSession>
) => {
  try {
    const docRef = await addDoc(collection(db, "refillSessions"), {
      ...sessionData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await updateDoc(docRef, { id: docRef.id });
    const newSession = await getDoc(docRef);
    return { ...newSession.data() } as RefillSession;
  } catch (e) {
    console.error("Error creating refill session: ", e);
    throw e;
  }
};

export const getRefillSession = async (sessionId: string, userId: string) => {
  try {
    const sessionRef = doc(db, "refillSessions", sessionId).withConverter(
      genericConverter<Partial<RefillSession>>()
    );
    const sessionDoc = await getDoc(sessionRef);
    if (sessionDoc.exists()) {
      const sessionData = sessionDoc.data();
      if (sessionData?.userId === userId) {
        return { ...sessionData, id: sessionId } as RefillSession;
      } else {
        throw new Error(
          "User does not have permission to access this session."
        );
      }
    } else {
      throw new Error("No such refill session!");
    }
  } catch (e) {
    console.error("Error getting refill session: ", e);
    throw e;
  }
};

export const getLatestRefillSessionByUser = async (
  userId: string,
  completed = false
) => {
  try {
    const q = query(
      collection(db, "refillSessions"),
      where("userId", "==", userId),
      where("completed", "==", completed),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const docQuery = await getDocs(q);
    if (docQuery.empty) {
      return null;
    }

    const doc = docQuery.docs[0];
    return { ...doc.data(), id: doc.id } as RefillSession;
  } catch (e) {
    console.error("Error getting refill session: ", e);
    throw e;
  }
};

export const getOrCreateRefillSession = async (
  userId: string
): Promise<RefillSession> => {
  const latest = await getLatestRefillSessionByUser(userId, false);
  if (latest) {
    return latest;
  }

  return createRefillSession({
    userId,
    completed: false,
    answers: {},
    status: "started",
  });
};

export const getOrCreateSurveySession = async (
  userId: string,
  surveyData: Partial<SurveySession> = {}
): Promise<Partial<SurveySession>> => {
  try {
    const q = query(
      collection(db, "surveySessions"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const sessionDoc = querySnapshot.docs[0];
      if (sessionDoc && sessionDoc.exists()) {
        return {
          id: sessionDoc.id,
          ...(sessionDoc.data() as Partial<SurveySession>),
        };
      } else {
        throw new Error("No such survey session!");
      }
    } else {
      const docRef = await addDoc(collection(db, "surveySessions"), {
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...surveyData,
      });
      const newSession = await getDoc(docRef);
      return {
        id: docRef.id,
        ...(newSession?.data() ?? {}),
      };
    }
  } catch (e) {
    console.error("Error getting or creating survey session: ", e);
    throw e;
  }
};

/**
 * Gets an existing survey session by completeness status.
 * Does NOT create a new session if one is not found - throws error instead.
 * Use this for async funnel where user should already have a complete session.
 *
 * If userEmail is provided and no session is found by userId, will search by email.
 * This handles cases where user recreated their account (new userId, same email).
 */
export const getSurveySessionByCompleteness = async (
  userId: string,
  completed = false,
  userEmail?: string,
  userBirthdate?: string
): Promise<{
  id: string;
  answers: IntakeAnswers;
  user: UserProfile;
}> => {
  try {
    // First, try to find by userId (current account)
    const q = query(
      collection(db, "surveySessions"),
      where("userId", "==", userId),
      where("completed", "==", completed),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const sessionDoc = querySnapshot.docs[0];
      if (sessionDoc && sessionDoc.exists()) {
        console.log(`Found survey session by userId: ${sessionDoc.id}`);
        return {
          id: sessionDoc.id,
          answers: sessionDoc.data().answers || {},
          user: sessionDoc.data().user || {},
        };
      }
    }

    // If not found by userId, try searching by user.user_id field
    // This handles the case where user recreated their account but user.user_id was updated
    console.log(`No survey found by userId, trying user.user_id: ${userId}`);
    const userIdFieldQuery = query(
      collection(db, "surveySessions"),
      where("user.user_id", "==", userId),
      where("completed", "==", completed),
      orderBy("createdAt", "desc"),
      limit(1)
    );
    const userIdFieldSnapshot = await getDocs(userIdFieldQuery);
    if (!userIdFieldSnapshot.empty) {
      const sessionDoc = userIdFieldSnapshot.docs[0];
      if (sessionDoc && sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        const oldUserId = sessionData.userId;
        console.log(
          `Found survey session by user.user_id from previous account. Old top-level userId: ${oldUserId}, Current userId: ${userId}`
        );

        // Update the top-level userId to match current account for future lookups
        if (oldUserId !== userId) {
          console.log(
            `Updating survey session ${sessionDoc.id} userId from ${oldUserId} to ${userId}`
          );
          try {
            await updateDoc(doc(db, "surveySessions", sessionDoc.id), {
              userId,
              updatedAt: serverTimestamp(),
            });
            console.log(`✅ Successfully updated userId for future lookups`);
          } catch (updateError) {
            console.error(
              `Failed to update userId for session ${sessionDoc.id}:`,
              updateError
            );
            // Don't throw - we still found the session, just couldn't update it
          }
        }

        return {
          id: sessionDoc.id,
          answers: sessionData.answers || {},
          user: sessionData.user || {},
        };
      }
    }

    // If still not found and email is provided, try searching by email as final fallback
    // This handles edge cases where user.user_id might not be set
    // DOB validation prevents matching sessions from reused emails
    if (userEmail) {
      const emailQuery = query(
        collection(db, "surveySessions"),
        where("user.email", "==", userEmail),
        where("completed", "==", completed),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      const emailSnapshot = await getDocs(emailQuery);
      if (!emailSnapshot.empty) {
        const sessionDoc = emailSnapshot.docs[0];
        if (sessionDoc && sessionDoc.exists()) {
          const sessionData = sessionDoc.data();
          const oldUserId = sessionData.userId;

          // Validate DOB matches if provided (prevents email reuse edge case)
          if (userBirthdate) {
            const sessionBirthdate =
              (sessionData.user as Record<string, unknown>)?.birthdate ||
              (sessionData.user as Record<string, unknown>)?.date_of_birth ||
              (sessionData.answers as Record<string, unknown>)?.date_of_birth;

            // Reject if DOB is missing from session OR doesn't match
            if (!sessionBirthdate || sessionBirthdate !== userBirthdate) {
              // DOB validation failed - log for security monitoring
              console.warn(
                `DOB validation failed for email lookup - ${!sessionBirthdate ? "missing DOB" : "mismatch"}`
              );

              // Security: Log DOB validation failures for monitoring
              const { datadogRum } = await import("@datadog/browser-rum");
              datadogRum.addAction("survey_session_dob_validation_failed", {
                tier: 3,
                reason: !sessionBirthdate ? "dob_missing" : "dob_mismatch",
                // Don't log actual DOB values (PII)
              });

              throw new Error(
                `No ${completed ? "complete" : "incomplete"} survey session found for user. Please complete the main questionnaire first.`
              );
            }
          }

          // Update the top-level userId to match current account for future lookups
          if (oldUserId !== userId) {
            try {
              await updateDoc(doc(db, "surveySessions", sessionDoc.id), {
                userId,
                updatedAt: serverTimestamp(),
              });
            } catch (updateError) {
              console.error(
                `Failed to update userId for session ${sessionDoc.id}:`,
                updateError
              );
              // Don't throw - we still found the session, just couldn't update it
            }
          }

          return {
            id: sessionDoc.id,
            answers: sessionData.answers || {},
            user: sessionData.user || {},
          };
        }
      }
    }

    throw new Error(
      `No ${completed ? "complete" : "incomplete"} survey session found for user. Please complete the main questionnaire first.`
    );
  } catch (e) {
    console.error("Error getting survey session: ", e);
    throw e;
  }
};

/**
 * Gets OR creates a survey session by completeness status.
 * Creates a new empty session if one is not found.
 * Use this for main funnel flows.
 */
export const getOrCreateSurveySessionByCompleteness = async (
  userId: string,
  completed = false
): Promise<{
  id: string;
  answers: IntakeAnswers;
  user: UserProfile;
}> => {
  try {
    const q = query(
      collection(db, "surveySessions"),
      where("userId", "==", userId),
      where("completed", "==", completed),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const sessionDoc = querySnapshot.docs[0];
      if (sessionDoc && sessionDoc.exists()) {
        return {
          id: sessionDoc.id,
          answers: sessionDoc.data().answers,
          user: sessionDoc.data().user,
        };
      } else {
        throw new Error("No such survey session!");
      }
    } else {
      const docRef = await addDoc(collection(db, "surveySessions"), {
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        completed: false,
        answers: {},
      });
      const newSession = await getDoc(docRef);
      return {
        id: docRef.id,
        answers: newSession?.data()?.answers ?? {},
        user: newSession?.data()?.user ?? {},
      };
    }
  } catch (e) {
    console.error("Error getting or creating survey session: ", e);
    throw e;
  }
};

export const markSurveySessionCompleted = async (sessionId: string) => {
  try {
    const sessionRef = doc(db, "surveySessions", sessionId);
    await updateDoc(sessionRef, {
      completed: true,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error marking survey session completed: ", e);
    throw e;
  }
};

export const getSurveySession = async (sessionId: string) => {
  try {
    const sessionRef = doc(db, "surveySessions", sessionId).withConverter(
      genericConverter<Partial<SurveySession>>()
    );
    const sessionDoc = await getDoc(sessionRef);
    if (sessionDoc.exists()) {
      return sessionDoc.data();
    } else {
      throw new Error("No such survey session!");
    }
  } catch (e) {
    console.error("Error getting survey session: ", e);
    throw e;
  }
};

export const getSurveySessionByUserId = async (
  userId: string
): Promise<SurveySession | null> => {
  try {
    const surveySessionsRef = collection(db, "surveySessions");
    const q = query(
      surveySessionsRef,
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      // Assuming you want the first matching document
      const docSnap = querySnapshot.docs[0];
      return {
        ...(docSnap.data() as SurveySession),
        id: docSnap.id,
      };
    } else {
      console.warn("No matching documents!");
      return null;
    }
  } catch (error) {
    console.error("Error getting survey session by userId:", error);
    throw error;
  }
};

export const getSurveyQuestion = async (
  slug: string,
  index: string
): Promise<IntakeQuestion | null> => {
  try {
    const questionsCollection = collection(db, "surveys", slug, "questions");
    const q = query(
      questionsCollection,
      where("index", "==", parseInt(index ?? "0"))
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const questionDoc = querySnapshot.docs[0];
      const questionData = questionDoc.data();

      // Fetch total number of questions
      const allQuestionsSnapshot = await getDocs(questionsCollection);
      const totalQuestions = allQuestionsSnapshot.size;
      return {
        index: questionData.index,
        slug: questionData.slug,
        totalQuestions,
        question_type: questionData.question_type,
        question: questionData.question,
        choices: questionData.choices,
        description: questionData.description,
        category: questionData.category,
      };
    } else {
      console.error("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error getting document:", error);
    return null;
  }
};

export const getConsult = async (guid: string) => {
  try {
    const consultRef = doc(db, "steadymd.consults", guid).withConverter(
      genericConverter<Partial<Consult>>()
    );
    const consultDoc = await getDoc(consultRef);
    if (consultDoc.exists()) {
      return consultDoc.data();
    } else {
      throw new Error("No such consult!");
    }
  } catch (e) {
    console.error("Error getting consult: ", e);
    throw e;
  }
};

export const getFollowUpOrSideEffectConsult = async (userId: string) => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("consultType", "==", "async_messaging"),
    where("reasonForVisit", "in", [
      ConsultReason.follow_up,
      ConsultReason.side_effect,
    ]),
    orderBy("createdAt", "desc"),
    limit(1)
  );

  // Execute the query
  const docQuery = await getDocs(q);
  if (docQuery.empty) {
    return null;
  }

  const doc = docQuery.docs[0];
  return { ...doc.data() } as SMDConsult;
};

export const getAllConsultsByType = async (
  userId: string,
  consultType: ConsultType
) => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("consultType", "==", consultType)
  );

  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    return [];
  }
  return querySnapshot.docs.map((doc) => doc.data() as Consult);
};

export const getLastScheduleConsultByTypeAndStatus = async (
  userId: string,
  consultType: ConsultType,
  status: ConsultVisitStatusEnum
) => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("consultType", "==", consultType),
    where("visitStatus", "==", status),
    orderBy("createdAt", "desc"),
    limit(1)
  );

  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    return [];
  }
  return querySnapshot.docs.map((doc) => doc.data() as Consult);
};

export const checkSingleFollowupExists = async (userId: string) => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("consultType", "==", "async_messaging"),
    where("reasonForVisit", "==", "follow_up"),
    where("visitStatus", "==", ConsultVisitStatusEnum.Completed),
    limit(2)
  );

  try {
    // Execute the query
    const querySnapshot = await getDocs(q);

    // Check if exactly one document exists
    if (querySnapshot.size === 1) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error checking documents:", error);
    return false;
  }
};

export const getRefillConsult = async (userId: string) => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("consultType", "==", "async_messaging"),
    where("reasonForVisit", "==", "follow_up"),
    where("visitStatus", "==", ConsultVisitStatusEnum.Completed),
    orderBy("createdAt", "desc"),
    limit(1)
  );

  // Execute the query
  const docQuery = await getDocs(q);
  if (docQuery.empty) {
    return null;
  }

  const doc = docQuery.docs[0];
  return { ...doc.data() } as SMDConsult;
};

export const getReferredOutConsult = async (userId: string) => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("visitStatus", "in", [
      ConsultVisitStatusEnum.ReferredOut,
      ConsultVisitStatusEnum.Rejected,
    ]),
    orderBy("createdAt", "desc"),
    limit(4) // Fetch more to account for rejected consults with clinician reschedule reason
  );

  const docQuery = await getDocs(q);
  if (docQuery.empty) {
    return null;
  }

  // Filter out rejected consults (all rejected consults are terminal states)
  // These should show the reschedule card or upcoming appointment card, not the referred out card
  const validConsults = docQuery.docs
    .map((doc) => ({ ...doc.data() }) as SMDConsult)
    .filter(
      (consult) => consult.visitStatus !== ConsultVisitStatusEnum.Rejected
    );

  return validConsults.length > 0 ? validConsults[0] : null;
};

export const getConsultByReason = async (
  userId: string,
  reason: ConsultReasonForVisitEnum
) => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("reasonForVisit", "==", reason),
    orderBy("createdAt", "desc"),
    limit(1)
  );

  const docQuery = await getDocs(q);
  if (docQuery.empty) {
    return null;
  }

  const doc = docQuery.docs[0];
  return { ...doc.data() } as SMDConsult;
};

export const getConsultsByUserId = async (userId?: string) => {
  if (!userId) {
    throw new Error("No userId in getConsultsByUserId");
  }

  try {
    const q = query(
      collection(db, "steadymd.consults"),
      where("userId", "==", userId)
    );

    const consults = await getDocs(q);

    if (!consults.empty) {
      return consults.docs.map((doc) => doc.data() as Consult);
    } else {
      console.warn("No documents available for this userId");
      return [];
    }
  } catch (e) {
    console.error("Error getting consults by userId: ", e);
    throw e;
  }
};

export const getLatestSyncConsult = async (userId?: string) => {
  if (!userId) throw new Error("no userId provided");
  try {
    const q = query(
      collection(db, "steadymd.consults"),
      where("userId", "==", userId),
      where("consultType", "in", [
        ConsultType.scheduled_video_visit,
        ConsultType.followup_video_visit,
      ]),
      where("visitStatus", "==", ConsultVisitStatusEnum.Completed),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const consults = await getDocs(q);

    if (consults.empty) {
      return null;
    } else {
      return consults.docs[0].data() as Consult;
    }
  } catch (e) {
    console.error("Error getting latest sync consult: ", e);
    throw e;
  }
};

export const updateConsult = async (guid: string, confirmed: boolean) => {
  try {
    const consultRef = doc(db, "steadymd.consults", guid);
    await updateDoc(consultRef, {
      confirmed,
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error updating consult: ", e);
    throw e;
  }
};

export const updatePrescriptionConfirmedDelivery = async (
  prescriptionId: string
) => {
  try {
    const prescription = doc(db, "steadymd.prescriptions", prescriptionId);
    const formattedDate = dayjs().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");

    await updateDoc(prescription, {
      confirmedDelivery: formattedDate,
      updatedAt: formattedDate,
    });
  } catch (e) {
    console.error("Error updating prescription: ", e);
    throw e;
  }
};

export const getSMDEpisode = async (episodeId: string) => {
  try {
    const episodeRef = doc(db, "steadymd.episodes", episodeId).withConverter(
      genericConverter<SMDEpisode>()
    );
    const episodeDoc = await getDoc(episodeRef);
    if (episodeDoc.exists()) {
      // return episode data
      return episodeDoc.data();
    } else {
      throw new Error("no such document");
    }
  } catch (err) {
    throw new Error(`Error getting episode: ${err}`);
  }
};

export const getLatestSMDPrescription = async (userId: string) => {
  const q = query(
    collection(db, "steadymd.prescriptions"),
    where("userId", "==", userId),
    orderBy("writtenDate", "desc"),
    limit(1)
  );

  const docQuery = await getDocs(q);
  if (docQuery.empty) {
    return null;
  }

  const doc = docQuery.docs[0];
  return { ...doc.data() } as Prescription;
};

export const getSMDPrescription = async (prescriptionId: string) => {
  try {
    const prescriptionRef = doc(
      db,
      "steadymd.prescriptions",
      prescriptionId
    ).withConverter(genericConverter<Prescription>());
    const prescriptionDoc = await getDoc(prescriptionRef);
    if (prescriptionDoc.exists()) {
      // return prescription data
      return prescriptionDoc.data();
    } else {
      console.warn("no such document");
    }
  } catch (err) {
    console.error(err);
    throw new Error(`Error getting prescription: ${err}`);
  }
};

const getSMDPrescriptions = async (userId: string) => {
  const q = query(
    collection(db, "steadymd.prescriptions"),
    where("userId", "==", userId),
    orderBy("writtenDate", "desc")
  );

  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    return querySnapshot.docs.map((doc) => doc.data());
  } else {
    console.warn("No prescriptions for this user");
    return [];
  }
};

export const getFirstDeliveredPrescription = async (userId?: string | null) => {
  if (!userId) throw new Error("no userId provided");
  try {
    const prescriptions = await getSMDPrescriptions(userId);
    if (prescriptions.length > 0) {
      // reverse array to get earlier prescriptions first. return when first delivered prescription is found
      for (const { prescriptionId } of prescriptions.reverse()) {
        const prescription = await getSMDPrescription(prescriptionId || "");
        const isDelivered =
          prescription?.confirmedDelivery ||
          (prescription?.deliveredDate &&
            isPastDate(prescription.deliveredDate));
        if (prescription && isDelivered) return prescription;
      }
    }
    return null;
  } catch (err) {
    console.error(err);
    throw new Error(`Error getting prescription: ${err}`);
  }
};

export const getStripePayments = async (userId: string) => {
  try {
    const stripePaymentsCollection = collection(
      db,
      "stripe.payments"
    ).withConverter(genericConverter<Partial<StripePayment>>());
    const q = query(stripePaymentsCollection, where("userId", "==", userId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      // return stripePayment data
      return querySnapshot.docs.map((doc) => doc.data());
    } else {
      throw new Error("no such document");
    }
  } catch (err) {
    console.log(err);
    throw new Error(`Error getting stripe payment: ${err}`);
  }
};

export const getStripeSubscriptions = async (userId: string) => {
  try {
    const q = query(
      collection(db, "stripe.subscriptions"),
      where("userId", "==", userId)
    );
    const docQuery = await getDocs(q);
    if (docQuery.empty) {
      return null;
    }
    return docQuery.docs.map((doc) => doc.data() as StripeSubscription);
  } catch (err) {
    console.error("Error fetching subscription: ", err);
    throw err;
  }
};

export const getAllSurveyQuestions = async (slug: string) => {
  try {
    const questionsCollection = collection(db, "surveys", slug, "questions");
    const querySnapshot = await getDocs(questionsCollection);

    if (!querySnapshot.empty) {
      const res: SurveyQuestionCollection = {};
      querySnapshot.forEach((doc) => {
        res[doc.id] = doc.data() as SurveyQuestion;
      });
      return res;
    } else {
      console.error("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error getting document:", error);
    return null;
  }
};

export const getIntake = async (userId: string) => {
  try {
    const q = query(
      collection(db, "steadymd.intakes"),
      where("userId", "==", userId),
      limit(1)
    );

    return getDocs(q);
  } catch (err) {
    console.error("Error fetching intake: ", err);
    throw err;
  }
};

export const getEpisodeByUser = async (userId?: string) => {
  if (!userId) {
    throw new Error("No userId provided");
  }
  try {
    const q = query(
      collection(db, "steadymd.episodes"),
      where("userId", "==", userId),
      limit(1)
    );
    const docQuery = await getDocs(q);
    if (docQuery.empty) {
      return null;
    }
    const doc = docQuery.docs[0];
    return { ...doc.data() } as FirestoreSMDEpisode;
  } catch (err) {
    console.error("Error fetching episode: ", err);
    throw err;
  }
};

export const setSideEffectsIntakeSubmitted = async (
  userId: string,
  value: boolean
): Promise<void> => {
  try {
    const q = query(
      collection(db, "steadymd.episodes"),
      where("userId", "==", userId),
      limit(1)
    );

    const docQuery = await getDocs(q);
    if (docQuery.empty) {
      throw new Error("No episode found for the user");
    }

    const episodeDoc = docQuery.docs[0];
    const episodeRef = doc(db, "steadymd.episodes", episodeDoc.id);

    const updatedDoc = await updateDoc(episodeRef, {
      sideEffectsIntakeSubmitted: value,
      updatedAt: serverTimestamp(),
    });

    return updatedDoc;
  } catch (error) {
    console.error("Error updating sideEffectsIntakeSubmitted: ", error);
    throw error;
  }
};

export const getLabOrdersSortedByDate = async (userId: string) => {
  try {
    const labOrdersCollection = collection(
      db,
      "steadymd.laborders"
    ).withConverter(genericConverter<FirestoreSMDLabOrder>());
    const q = query(
      labOrdersCollection,
      where("userId", "==", userId),
      orderBy("createdDate", "desc")
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      // return lab order data
      return querySnapshot.docs.map((doc) => doc.data());
    } else {
      throw new Error("no such documents");
    }
  } catch (err) {
    console.log(err);
    throw new Error(`Error getting lab orders: ${err}`);
  }
};

export const getMedicationByNDC = async (ndc: string | undefined) => {
  if (!ndc) {
    throw new Error("No NDC provided");
  }
  try {
    const medicationRef = doc(db, "medications", ndc).withConverter(
      genericConverter<Medication>()
    );
    const querySnapshot = await getDoc(medicationRef);
    if (querySnapshot.exists()) {
      return querySnapshot.data();
    } else {
      console.warn("No such document");
    }
  } catch (err) {
    console.error(err);
    throw new Error(`Error getting medication: ${err}`);
  }
};

export const getMedicationById = async (id: string | undefined) => {
  if (!id) {
    throw new Error("No id provided");
  }
  try {
    const medicationRef = doc(db, "medications", id).withConverter(
      genericConverter<Medication>()
    );
    const querySnapshot = await getDoc(medicationRef);
    if (querySnapshot.exists()) {
      return querySnapshot.data();
    } else {
      console.warn("No such document");
    }
  } catch (err) {
    console.error(err);
    throw new Error(`Error getting medication: ${err}`);
  }
};

export const getNotificationsByUser = async (
  userId: string | undefined,
  unreadOnly: boolean
): Promise<FirestoreNotification[]> => {
  try {
    let q;

    if (unreadOnly) {
      q = query(
        collection(db, "notifications"),
        where("userId", "==", userId),
        where("read", "==", false),
        orderBy("timestampSent", "desc")
      );
    } else {
      q = query(
        collection(db, "notifications"),
        where("userId", "==", userId),
        orderBy("timestampSent", "desc")
      );
    }
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return querySnapshot.docs.map(
        (doc) => doc.data() as FirestoreNotification
      );
    }
    return [];
  } catch (err) {
    console.error(err);
    throw new Error(`Error getting messages: ${err}`);
  }
};

export const markAllMessagesRead = async (userId: string | undefined) => {
  try {
    const q = query(
      collection(db, "steadymd.messages"),
      where("userId", "==", userId),
      where("read", "==", false)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const batch = writeBatch(db);
      querySnapshot.docs.forEach((messageDoc) => {
        const docRef = doc(db, "steadymd.messages", messageDoc.id);
        batch.update(docRef, { read: true });
      });
      await batch.commit();
    }
  } catch (err) {
    console.error(err);
    throw new Error(`Error setting messages to read: ${err}`);
  }
};

export const markAllNotificationsRead = async (userId: string | undefined) => {
  try {
    const q = query(
      collection(db, "notifications"),
      where("userId", "==", userId),
      where("read", "==", false)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const batch = writeBatch(db);
      querySnapshot.docs.forEach((notificationDoc) => {
        const docRef = doc(db, "notifications", notificationDoc.id);
        batch.update(docRef, { read: true });
      });
      await batch.commit();
    } else {
      throw new Error("no such documents");
    }
  } catch (err) {
    console.error(err);
    throw new Error(`Error getting messages: ${err}`);
  }
};

export const updateUserPassword = async (
  user: User | null,
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<void> => {
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Create credential for re-authentication
  const credential = EmailAuthProvider.credential(email, currentPassword);

  try {
    // Re-authenticate the user
    await reauthenticateWithCredential(user, credential);

    // Update the user's password
    await updatePassword(user, newPassword);
  } catch (err) {
    console.error(err);
    throw new Error(`Error resetting password: ${err}`);
  }
};

export const getSurveyBySlug = async (slug: string) => {
  try {
    const surveyRef = doc(db, "surveys", slug);
    const docSnap = await getDoc(surveyRef);
    return docSnap.data();
  } catch (err) {
    console.error(err);
    throw new Error(`Error retrieving survey: ${err}`);
  }
};

export const checkExistingAsyncMessagingConsult = async (
  userId: string
): Promise<boolean> => {
  try {
    const consultsRef = collection(db, "steadymd.consults");
    const q = query(
      consultsRef,
      where("userId", "==", userId),
      where("consultType", "==", ConsultType.async_messaging),
      where("visitStatus", "in", [
        "received",
        "unassigned",
        "assigned",
        "in_progress",
        "ready_to_resume",
        "waiting",
        "external_in_progress",
        "in_call",
        "issue",
        "scheduled",
        "in_review",
        "waiting_for_intake",
        "waiting_to_schedule",
        "clinician_change",
        // Excluded: "completed", "rejected", "referred_out", "canceled", "patient_abandoned", "visit_removed"
        // Excluded: All lab statuses
      ])
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error("Error checking existing consults:", error);
    throw error;
  }
};

// Add async session methods
export const getAsyncSession = async (
  sessionId: string
): Promise<AsyncSessionData | null> => {
  try {
    const docRef = doc(db, "asyncSessions", sessionId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as AsyncSessionData;
    }
    return null;
  } catch (error) {
    console.error("Error getting async session:", error);
    throw error;
  }
};

export const updateAsyncSession = async (
  payload: UpdateAsyncSessionPayload
): Promise<void> => {
  try {
    const { id, ...updateData } = payload;
    const docRef = doc(db, "asyncSessions", id);

    await updateDoc(docRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error updating async session:", error);
    throw error;
  }
};

/**
 * Acquires a lock for consult creation by atomically setting a "creating" flag
 * This prevents multiple requests from creating consults simultaneously
 * @returns success: true if lock acquired, false if already locked/created
 */
export const acquireConsultCreationLock = async (
  asyncSessionId: string
): Promise<{
  success: boolean;
  existingConsultGuid?: string;
  alreadyCreating?: boolean;
}> => {
  const { runTransaction, doc } = await import("firebase/firestore");
  const docRef = doc(db, "asyncSessions", asyncSessionId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sessionDoc = await transaction.get(docRef);

      if (!sessionDoc.exists()) {
        throw new Error("AsyncSession not found");
      }

      const currentData = sessionDoc.data();

      // Check if consult already exists
      if (currentData?.consultGuid) {
        return {
          success: false,
          existingConsultGuid: currentData.consultGuid,
        };
      }

      // Check if another request is currently creating
      if (currentData?.consultCreating === true) {
        // Check if lock is stale (older than 2 minutes)
        const lockTimestamp = currentData?.consultCreatingAt;
        const lockAge = lockTimestamp?.toDate?.();
        const isStale =
          lockAge && Date.now() - lockAge.getTime() > 2 * 60 * 1000;

        if (isStale) {
          // Expired lock - acquire it
          console.warn(
            "Acquiring stale consult lock, previous holder may have crashed"
          );
          transaction.update(docRef, {
            consultCreating: true,
            consultCreatingAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          return { success: true };
        }

        return {
          success: false,
          alreadyCreating: true,
        };
      }

      // Acquire the lock by setting consultCreating flag
      transaction.update(docRef, {
        consultCreating: true,
        consultCreatingAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    console.error("Error in acquireConsultCreationLock:", error);
    throw error;
  }
};

/**
 * Atomically sets consultGuid in asyncSession if not already set
 * Uses Firestore transaction to prevent race conditions
 * @returns true if consultGuid was set, false if already exists, existing consultGuid if found
 */
export const atomicSetConsultGuid = async (
  asyncSessionId: string,
  consultGuid: string
): Promise<{ success: boolean; existingConsultGuid?: string }> => {
  const { runTransaction, doc } = await import("firebase/firestore");
  const docRef = doc(db, "asyncSessions", asyncSessionId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sessionDoc = await transaction.get(docRef);

      if (!sessionDoc.exists()) {
        throw new Error("AsyncSession not found");
      }

      const currentData = sessionDoc.data();
      const existingConsultGuid = currentData?.consultGuid;

      if (existingConsultGuid) {
        // Already has a consultGuid - another request won the race
        return { success: false, existingConsultGuid };
      }

      // Atomically set the consultGuid and clear the creating flag
      transaction.update(docRef, {
        consultGuid,
        consultCreating: false,
        consultCreatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    console.error("Error in atomicSetConsultGuid:", error);
    throw error;
  }
};

/**
 * Acquires a lock for episode creation by atomically setting a "creating" flag
 * This prevents multiple requests from creating episodes simultaneously
 * @returns success: true if lock acquired, false if already locked/created
 */
export const acquireEpisodeCreationLock = async (
  asyncSessionId: string
): Promise<{
  success: boolean;
  existingEpisodeGuid?: string;
  existingPatientGuid?: string;
  alreadyCreating?: boolean;
}> => {
  const { runTransaction, doc } = await import("firebase/firestore");
  const docRef = doc(db, "asyncSessions", asyncSessionId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sessionDoc = await transaction.get(docRef);

      if (!sessionDoc.exists()) {
        throw new Error("AsyncSession not found");
      }

      const currentData = sessionDoc.data();

      // Check if episode already exists
      if (currentData?.episodeGuid && currentData?.patientGuid) {
        return {
          success: false,
          existingEpisodeGuid: currentData.episodeGuid,
          existingPatientGuid: currentData.patientGuid,
        };
      }

      // Check if another request is currently creating
      if (currentData?.episodeCreating === true) {
        // Check if lock is stale (older than 2 minutes)
        const lockTimestamp = currentData?.episodeCreatingAt;
        const lockAge = lockTimestamp?.toDate?.();
        const isStale =
          lockAge && Date.now() - lockAge.getTime() > 2 * 60 * 1000;

        if (isStale) {
          // Expired lock - acquire it
          console.warn(
            "Acquiring stale episode lock, previous holder may have crashed"
          );
          transaction.update(docRef, {
            episodeCreating: true,
            episodeCreatingAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          return { success: true };
        }

        return {
          success: false,
          alreadyCreating: true,
        };
      }

      // Acquire the lock by setting episodeCreating flag
      transaction.update(docRef, {
        episodeCreating: true,
        episodeCreatingAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    console.error("Error in acquireEpisodeCreationLock:", error);
    throw error;
  }
};

/**
 * Atomically sets episodeGuid and patientGuid in asyncSession if not already set
 * Uses Firestore transaction to prevent race conditions
 * @returns success status and existing GUIDs if already set
 */
export const atomicSetEpisodeAndPatientGuids = async (
  asyncSessionId: string,
  episodeGuid: string,
  patientGuid: string
): Promise<{
  success: boolean;
  existingEpisodeGuid?: string;
  existingPatientGuid?: string;
}> => {
  const { runTransaction, doc } = await import("firebase/firestore");
  const docRef = doc(db, "asyncSessions", asyncSessionId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sessionDoc = await transaction.get(docRef);

      if (!sessionDoc.exists()) {
        throw new Error("AsyncSession not found");
      }

      const currentData = sessionDoc.data();
      const existingEpisodeGuid = currentData?.episodeGuid;
      const existingPatientGuid = currentData?.patientGuid;

      if (existingEpisodeGuid && existingPatientGuid) {
        // Already has episode and patient GUIDs - another request won the race
        return {
          success: false,
          existingEpisodeGuid,
          existingPatientGuid,
        };
      }

      // Atomically set both GUIDs and clear the creating flag
      transaction.update(docRef, {
        episodeGuid,
        patientGuid,
        episodeCreating: false,
        episodeCreatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    console.error("Error in atomicSetEpisodeAndPatientGuids:", error);
    throw error;
  }
};

/**
 * Acquires a lock for intake creation by setting intakeCreating flag in asyncSession
 * Uses Firestore transaction to prevent race conditions
 * @returns success status, existing intakeGuid if already set, or alreadyCreating flag
 */
export const acquireIntakeCreationLock = async (
  asyncSessionId: string
): Promise<{
  success: boolean;
  existingIntakeGuid?: string;
  alreadyCreating?: boolean;
}> => {
  const { runTransaction, doc } = await import("firebase/firestore");
  const docRef = doc(db, "asyncSessions", asyncSessionId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sessionDoc = await transaction.get(docRef);

      if (!sessionDoc.exists()) {
        throw new Error("AsyncSession not found");
      }

      const currentData = sessionDoc.data();

      // Check if intake already exists
      if (currentData?.intakeGuid) {
        return {
          success: false,
          existingIntakeGuid: currentData.intakeGuid,
        };
      }

      // Check if another request is currently creating
      if (currentData?.intakeCreating === true) {
        // Check if lock is stale (older than 2 minutes)
        const lockTimestamp = currentData?.intakeCreatingAt;
        const lockAge = lockTimestamp?.toDate?.();
        const isStale =
          lockAge && Date.now() - lockAge.getTime() > 2 * 60 * 1000;

        if (isStale) {
          // Expired lock - acquire it
          console.warn(
            "Acquiring stale intake lock, previous holder may have crashed"
          );
          transaction.update(docRef, {
            intakeCreating: true,
            intakeCreatingAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          return { success: true };
        }

        return {
          success: false,
          alreadyCreating: true,
        };
      }

      // Acquire the lock by setting intakeCreating flag
      transaction.update(docRef, {
        intakeCreating: true,
        intakeCreatingAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    console.error("Error in acquireIntakeCreationLock:", error);
    throw error;
  }
};

/**
 * Atomically sets intakeGuid in asyncSession if not already set
 * Uses Firestore transaction to prevent race conditions
 * Clears the intakeCreating lock flag
 * @returns success status and existing intakeGuid if already set
 */
export const atomicSetIntakeGuid = async (
  asyncSessionId: string,
  intakeGuid: string
): Promise<{ success: boolean; existingIntakeGuid?: string }> => {
  const { runTransaction, doc } = await import("firebase/firestore");
  const docRef = doc(db, "asyncSessions", asyncSessionId);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sessionDoc = await transaction.get(docRef);

      if (!sessionDoc.exists()) {
        throw new Error("AsyncSession not found");
      }

      const currentData = sessionDoc.data();

      // Check if intakeGuid is already set
      if (currentData?.intakeGuid && currentData.intakeGuid !== intakeGuid) {
        return {
          success: false,
          existingIntakeGuid: currentData.intakeGuid,
        };
      }

      // Set the intakeGuid and clear the creating lock
      transaction.update(docRef, {
        intakeGuid,
        intakeCreating: false,
        intakeCreatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    });

    return result;
  } catch (error) {
    console.error("Error in atomicSetIntakeGuid:", error);
    throw error;
  }
};

/**
 * Generic helper to release a creation lock without setting a GUID
 * Used when creation fails to prevent stuck locks
 * @param asyncSessionId - The async session ID
 * @param lockField - The lock field name to clear (intakeCreating, consultCreating, or episodeCreating)
 * @param lockType - Human-readable lock type for logging (intake, consult, or episode)
 */
const releaseCreationLock = async (
  asyncSessionId: string,
  lockField: "intakeCreating" | "consultCreating" | "episodeCreating",
  lockType: "intake" | "consult" | "episode"
): Promise<void> => {
  const { runTransaction, doc } = await import("firebase/firestore");
  const docRef = doc(db, "asyncSessions", asyncSessionId);

  try {
    await runTransaction(db, async (transaction) => {
      const sessionDoc = await transaction.get(docRef);

      if (!sessionDoc.exists()) {
        console.warn("AsyncSession not found when releasing lock");
        return;
      }

      const currentData = sessionDoc.data();

      // Only clear the lock if it's currently set
      if (currentData?.[lockField] === true) {
        transaction.update(docRef, {
          [lockField]: false,
          updatedAt: serverTimestamp(),
        });
        console.log(
          `Released ${lockType} creation lock for session:`,
          asyncSessionId
        );
      }
    });
  } catch (error) {
    console.error(`Error releasing ${lockType} creation lock:`, error);
    // Don't throw - we're already in an error state, don't make it worse
  }
};

/**
 * Releases the intake creation lock without setting an intakeGuid
 * Used when intake creation fails to prevent stuck locks
 * @param asyncSessionId - The async session ID
 */
export const releaseIntakeCreationLock = async (
  asyncSessionId: string
): Promise<void> => {
  return releaseCreationLock(asyncSessionId, "intakeCreating", "intake");
};

/**
 * Releases the consult creation lock without setting a consultGuid
 * Used when consult creation fails to prevent stuck locks
 * @param asyncSessionId - The async session ID
 */
export const releaseConsultCreationLock = async (
  asyncSessionId: string
): Promise<void> => {
  return releaseCreationLock(asyncSessionId, "consultCreating", "consult");
};

/**
 * Releases the episode creation lock without setting episodeGuid/patientGuid
 * Used when episode creation fails to prevent stuck locks
 * @param asyncSessionId - The async session ID
 */
export const releaseEpisodeCreationLock = async (
  asyncSessionId: string
): Promise<void> => {
  return releaseCreationLock(asyncSessionId, "episodeCreating", "episode");
};

/**
 * Retrieves the async funnel questions and metadata from Firestore
 * @returns Promise resolving to questions object and total text questions count
 */
export const getAsyncFunnelQuestions = async (): Promise<{
  questions: Record<string, IntakeQuestion>;
  totalTextQuestions: number;
}> => {
  // Get the async-funnel document to read totalTextQuestions
  const asyncFunnelDoc = doc(db, "surveys", "async-funnel");
  const asyncFunnelSnapshot = await getDoc(asyncFunnelDoc);
  const asyncFunnelData = asyncFunnelSnapshot.data();
  const totalTextQuestions = asyncFunnelData?.totalTextQuestions || 0;

  // Get the questions subcollection
  const asyncQuestionsCollection = collection(
    db,
    "surveys",
    "async-funnel",
    "questions"
  );
  const querySnapshot = await getDocs(asyncQuestionsCollection);

  const questions: Record<string, IntakeQuestion> = {};
  querySnapshot.forEach((doc) => {
    questions[doc.id] = doc.data() as IntakeQuestion;
  });

  return {
    questions,
    totalTextQuestions,
  };
};

// Get or create async session with atomic operations to prevent race conditions
export const getOrCreateAsyncSession = async (
  userId: string
): Promise<AsyncSessionStatus> => {
  if (!userId) {
    throw new Error("User ID is required");
  }

  try {
    // Query for existing sessions for this user
    const sessionsRef = collection(db, "asyncSessions");
    const q = query(
      sessionsRef,
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const existingSessions = await getDocs(q);

    if (!existingSessions.empty) {
      const existingSession = existingSessions.docs[0];
      const sessionData = existingSession.data();

      if (sessionData.status === "completed") {
        return {
          sessionId: null,
          status: "completed" as const,
          canProceed: false,
          message:
            "You have already completed the health assessment. Thank you!",
        };
      } else {
        return {
          sessionId: existingSession.id,
          status: sessionData.status as "not_started" | "started",
          canProceed: true,
          message: "Continuing your previous assessment...",
        };
      }
    }

    // Create new session - use addDoc for consistency with other sessions
    const newSessionData = {
      userId,
      answers: {},
      status: "not_started",
      currentQuestionSlug: "tried_weight_loss",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(
      collection(db, "asyncSessions"),
      newSessionData
    );

    return {
      sessionId: docRef.id,
      status: "not_started" as const,
      canProceed: true,
      message: "Starting new health assessment...",
    };
  } catch (error) {
    console.error("Error in getOrCreateAsyncSession:", error);
    throw error;
  }
};

// Method to get the current question for an existing session
export const getCurrentQuestionForSession = async (
  sessionId: string
): Promise<string> => {
  try {
    const sessionRef = doc(db, "asyncSessions", sessionId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
      throw new Error("Session not found");
    }

    const sessionData = sessionDoc.data();
    return sessionData.currentQuestionSlug || "tried_weight_loss";
  } catch (error) {
    console.error("Error getting current question:", error);
    throw error;
  }
};

export const updateEpisode = async (
  episodeGuid: string,
  data: Partial<Episode> | Partial<SMDEpisode> | Partial<FirestoreSMDEpisode>
) => {
  try {
    const episodeRef = doc(db, "steadymd.episodes", episodeGuid);
    await updateDoc(episodeRef, { ...data, updatedAt: serverTimestamp() });
  } catch (err) {
    console.error("Error updating episode:", err);
    throw err;
  }
};
